import React, { useEffect, useMemo, useState } from "react";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { classNames } from "../utils";

export type MediaExternalInternalURLs = {
  external_url: string;
  internal_url?: string;
  type: string;
};

export type PostImageProps = {
  media: MediaExternalInternalURLs[];
  noPadding?: boolean;
};

export function contentMediaToMediaExternalInternal(content: {
  media?: MediaExternalInternalURLs[];
}) {
  const postImageUrls: MediaExternalInternalURLs[] = content.media
    ? content.media
        .filter((pivot) => pivot.type === "image")
        .map((m: MediaExternalInternalURLs) => m)
    : [];
  return postImageUrls;
}

function mediaKey(media: MediaExternalInternalURLs, idx: number): string {
  const internal = (media.internal_url ?? "").trim();
  const external = (media.external_url ?? "").trim();
  return internal || external || String(idx);
}

export const PostImage = ({ media, noPadding = false }: PostImageProps) => {
  if (!media || (media && media.length === 0)) {
    return <></>;
  }
  return (
    <div
      className={classNames(
        "w-80 print:w-full print:grid print:grid-cols-4 print:gap-4",
        noPadding ? "" : "p-4"
      )}
    >
      {media.map((pivot, idx) => (
        <ImageWithFallback key={mediaKey(pivot, idx)} media={pivot} />
      ))}
    </div>
  );
};

export const PostImageStats = ({
  media,
  noPadding = false,
}: PostImageProps) => {
  if (!media || (media && media.length === 0)) {
    return <></>;
  }
  return (
    <>
      {media.map((pivot, idx) => (
        <div
          key={mediaKey(pivot, idx)}
          className="h-72 w-72 print:h-52 print:w-52 print:rounded-md relative"
        >
          <ImageWithFallback media={pivot} isStats={true} />
        </div>
      ))}
    </>
  );
};

function ImagePlaceholder({ isStats }: { isStats?: boolean }) {
  if (isStats) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-md"
        role="img"
        aria-label="Image unavailable"
      >
        <PhotoIcon className="w-16 h-16 text-gray-400" aria-hidden />
      </div>
    );
  }
  return (
    <div
      className="mb-3 rounded-xl w-full min-h-[200px] bg-gray-100 flex items-center justify-center"
      role="img"
      aria-label="Image unavailable"
    >
      <PhotoIcon className="w-12 h-12 text-gray-400" aria-hidden />
    </div>
  );
}

type LoadPhase = "primary" | "secondary" | "placeholder";

function resolveImageSources(media: MediaExternalInternalURLs): {
  primary: string;
  secondary: string | null;
} {
  const internal = (media.internal_url ?? "").trim();
  const external = (media.external_url ?? "").trim();

  if (internal && external && internal !== external) {
    return { primary: internal, secondary: external };
  }
  if (internal) {
    return { primary: internal, secondary: null };
  }
  if (external) {
    return { primary: external, secondary: null };
  }
  return { primary: "", secondary: null };
}

const ImageWithFallback = ({
  media,
  isStats = false,
}: {
  media: MediaExternalInternalURLs;
  isStats?: boolean;
}) => {
  const { primary, secondary } = useMemo(
    () => resolveImageSources(media),
    [media]
  );

  const [phase, setPhase] = useState<LoadPhase>("primary");

  useEffect(() => {
    setPhase("primary");
  }, [primary, secondary]);

  if (!primary) {
    return <></>;
  }

  const src =
    phase === "primary"
      ? primary
      : phase === "secondary" && secondary
      ? secondary
      : null;

  if (phase === "placeholder" || src === null) {
    return <ImagePlaceholder isStats={isStats} />;
  }

  const className = isStats
    ? "max-w-[10%] max-h-[10%] rounded-[1px] scale-[10] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    : "mb-3 rounded-xl w-full";

  return (
    <img
      src={src}
      alt=""
      className={className}
      onError={() => {
        if (phase === "primary" && secondary) {
          setPhase("secondary");
          return;
        }
        setPhase("placeholder");
      }}
    />
  );
};
